import React, { useState, useEffect } from 'react';
import { getTestimonials, createTestimonial, updateTestimonial, toggleTestimonialVisibility, deleteTestimonial } from '../../api/adminApi';
import Modal from '../../components/admin/Modal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';

function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, testimonial: null });

  const [formData, setFormData] = useState({
    author_name: '',
    content: '',
    rating: 5,
    review_date: '',
    display_order: 0,
    is_visible: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const data = await getTestimonials();
      setTestimonials(data);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      alert('Erreur de chargement des avis');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTestimonial(null);
    setFormData({
      author_name: '',
      content: '',
      rating: 5,
      review_date: new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
      display_order: testimonials.length,
      is_visible: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      author_name: testimonial.author_name,
      content: testimonial.content,
      rating: testimonial.rating,
      review_date: testimonial.review_date || '',
      display_order: testimonial.display_order || 0,
      is_visible: testimonial.is_visible
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTestimonial(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const dataToSend = {
        ...formData,
        rating: parseInt(formData.rating)
      };

      if (editingTestimonial) {
        await updateTestimonial(editingTestimonial.id, dataToSend);
      } else {
        await createTestimonial(dataToSend);
      }

      await fetchTestimonials();
      closeModal();
    } catch (err) {
      console.error('Error saving testimonial:', err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleVisibility = async (testimonial) => {
    try {
      await toggleTestimonialVisibility(testimonial.id);
      setTestimonials(testimonials.map(t =>
        t.id === testimonial.id ? { ...t, is_visible: !t.is_visible } : t
      ));
    } catch (err) {
      console.error('Error toggling visibility:', err);
      alert('Erreur lors du changement de visibilité');
    }
  };

  const handleDeleteClick = (testimonial) => {
    setDeleteDialog({ isOpen: true, testimonial });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTestimonial(deleteDialog.testimonial.id);
      setTestimonials(testimonials.filter(t => t.id !== deleteDialog.testimonial.id));
      setDeleteDialog({ isOpen: false, testimonial: null });
    } catch (err) {
      console.error('Error deleting testimonial:', err);
      alert('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ color: '#2c3e50' }}>Avis Clients ({testimonials.length})</h1>
        <button
          onClick={openCreateModal}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#9c6f44',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          + Nouvel avis
        </button>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Auteur</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Avis</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Note</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Visible</th>
              <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {testimonials.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
                  Aucun avis trouvé
                </td>
              </tr>
            ) : (
              testimonials.map(testimonial => (
                <tr key={testimonial.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{testimonial.author_name}</td>
                  <td style={{ padding: '1rem', maxWidth: '300px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {testimonial.content}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ color: '#ffd700' }}>
                      {"★".repeat(testimonial.rating)}{"☆".repeat(5 - testimonial.rating)}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', color: '#7f8c8d' }}>{testimonial.review_date}</td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => handleToggleVisibility(testimonial)}
                      style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: testimonial.is_visible ? '#d4edda' : '#f8d7da',
                        color: testimonial.is_visible ? '#155724' : '#721c24',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        cursor: 'pointer'
                      }}
                    >
                      {testimonial.is_visible ? 'Visible' : 'Masqué'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    <button
                      onClick={() => openEditModal(testimonial)}
                      style={{
                        padding: '0.5rem 1rem',
                        marginRight: '0.5rem',
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Éditer
                    </button>
                    <button
                      onClick={() => handleDeleteClick(testimonial)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingTestimonial ? 'Modifier l\'avis' : 'Nouvel avis'}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Nom de l'auteur *
            </label>
            <input
              type="text"
              name="author_name"
              value={formData.author_name}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Avis *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              required
              rows="4"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Note *
            </label>
            <select
              name="rating"
              value={formData.rating}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="5">⭐⭐⭐⭐⭐ (5 étoiles)</option>
              <option value="4">⭐⭐⭐⭐ (4 étoiles)</option>
              <option value="3">⭐⭐⭐ (3 étoiles)</option>
              <option value="2">⭐⭐ (2 étoiles)</option>
              <option value="1">⭐ (1 étoile)</option>
            </select>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Date de l'avis
            </label>
            <input
              type="text"
              name="review_date"
              value={formData.review_date}
              onChange={handleInputChange}
              placeholder="janvier 2024"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_visible"
                checked={formData.is_visible}
                onChange={handleInputChange}
                style={{ marginRight: '0.5rem' }}
              />
              <span>Visible sur le site</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={closeModal}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#9c6f44',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, testimonial: null })}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer l'avis de "${deleteDialog.testimonial?.author_name}" ?`}
      />
    </div>
  );
}

export default TestimonialsPage;
